import { Close as CloseIcon, Upload as UploadIcon } from "@mui/icons-material";
import { Button, IconButton, Stack } from "@mui/material";
import React, { useCallback } from "react";
import { Accept } from "react-dropzone";

import { useBoolean } from "../../../hooks/useBoolean";
import { useUploadApplicationDocument } from "../../../queries/accounts";
import { assertNever } from "../../../utils/assertNever";
import { FileDragAndDropInput } from "../../input/FileDragAndDropInput";

const DOCUMENT_TYPES: Accept = {
    "application/pdf": [],
    "image/jpeg": [],
    "image/png": [],
};

export enum DocumentSide {
    Front = "front",
    Back = "back",
}

interface IProps {
    applicationId: string;
    side?: DocumentSide;
    documentId: string;
    maxFileSizeBytes: number;
}

export const UploadDocumentButton: React.FC<IProps> = ({ applicationId, side, documentId, maxFileSizeBytes }) => {
    const [shouldShowDnd, , { setTrue: showDnd, setFalse: hideDnd }] = useBoolean();

    const { isLoading, mutate: uploadDocument } = useUploadApplicationDocument();

    const handleAdd = useCallback(
        ([file]: File[]): void => {
            uploadDocument({ application_id: applicationId, back: isDocumentSideBack(side), doc_id: documentId, file });
            hideDnd();
        },
        [applicationId, side, documentId, hideDnd, uploadDocument],
    );

    function getDocumentTitle(): string {
        switch (side) {
            case undefined:
                return "Files";
            case DocumentSide.Front:
                return "Front";
            case DocumentSide.Back:
                return "Back";
            default:
                return assertNever(side);
        }
    }

    if (shouldShowDnd) {
        return (
            <Stack>
                <IconButton onClick={hideDnd} size="small" sx={{ alignSelf: "flex-end" }}>
                    <CloseIcon fontSize="inherit" />
                </IconButton>
                <FileDragAndDropInput
                    acceptedFileTypes={DOCUMENT_TYPES}
                    maxFiles={1}
                    maxSize={maxFileSizeBytes}
                    minimal={true}
                    onDropAccepted={handleAdd}
                />
            </Stack>
        );
    }

    return (
        <Button loading={isLoading} onClick={showDnd} startIcon={<UploadIcon fontSize="inherit" />} variant="outlined">
            Upload {getDocumentTitle()}
        </Button>
    );
};

function isDocumentSideBack(side: DocumentSide | undefined): boolean {
    switch (side) {
        case undefined:
        case DocumentSide.Front:
            return false;
        case DocumentSide.Back:
            return true;
        default:
            return assertNever(side);
    }
}
